import L from 'leaflet';
import { BuildingSummary } from '../types/protocols';
import { BuildingType } from '../types/BuildingTypeEnums';

/** A building's marker colour on the maps (the overview and the building page). */
export const BUILDING_MARKER_COLOR = '#001D60';

const MARKER_GROUP_TAG_SYMBOLS: Array<{ prefix: string; url: string }> = [
    { prefix: 'birdtower',  url: '/Images/UI/marker-birdtower.png' },
    { prefix: 'shelter',    url: '/Images/UI/marker-shelter.png' },
    { prefix: 'footbridge', url: '/Images/UI/marker-footbridge.png' },
    { prefix: 'bridge',     url: '/Images/UI/marker-bridge.png' },
    { prefix: 'green-area', url: '/Images/UI/marker-greenarea.png' },
];

/** The symbol inside a building's map marker: a group-tag symbol, a toilet or a plain building. */
export function markerSymbolUrl(building: BuildingSummary): string {
    const tags = Object.values(building.groupTags ?? {});
    for (const { prefix, url } of MARKER_GROUP_TAG_SYMBOLS) {
        if (tags.some(t => t.tag.startsWith(prefix))) return url;
    }
    const isToilet = building.buildingType === BuildingType.toiletArchipelago ||
                     building.buildingType === BuildingType.toiletReserve;
    return isToilet ? '/Images/UI/marker-toilet.png' : '/Images/UI/marker-building.png';
}

const ICON_SIZE = 40;
const ICON_RADIUS = 18;
const ICON_IMAGE_SIZE = 20;
const ICON_IMAGE_OFFSET = (ICON_SIZE - ICON_IMAGE_SIZE) / 2;
const TAIL_LENGTH = 12;
const TOTAL_HEIGHT = ICON_SIZE + TAIL_LENGTH;
const CX = ICON_SIZE / 2;
const CY = ICON_SIZE / 2;

const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const createSvgMarker = (color: string, symbolUrl: string, label?: string): L.DivIcon => {
    const truncated = label && label.length > 20 ? label.slice(0, 20) + '…' : label;
    const labelHtml = truncated ? `<span class="map-marker-label">${escapeHtml(truncated)}</span>` : '';
    const html = `
        <div class="map-marker-inner">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${ICON_SIZE} ${TOTAL_HEIGHT}" width="${ICON_SIZE}" height="${TOTAL_HEIGHT}" style="filter: drop-shadow(0px 2px 4px rgba(0,0,0,0.4));">
                <line x1="${CX}" y1="${CY + ICON_RADIUS - 1}" x2="${CX}" y2="${TOTAL_HEIGHT}" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                <circle cx="${CX}" cy="${CY}" r="${ICON_RADIUS}" fill="${color}" stroke="white" stroke-width="2.5" />
                <image href="${symbolUrl}" x="${ICON_IMAGE_OFFSET}" y="${ICON_IMAGE_OFFSET}" width="${ICON_IMAGE_SIZE}" height="${ICON_IMAGE_SIZE}" style="filter: brightness(0) invert(1);" />
            </svg>
        </div>
        ${labelHtml}`;

    return L.divIcon({
        className: 'map-marker',
        html,
        iconSize: [ICON_SIZE, TOTAL_HEIGHT],
        iconAnchor: [CX, TOTAL_HEIGHT],
        popupAnchor: [0, -TOTAL_HEIGHT - 4],
    });
};
